import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { colors, fonts } from '../brand';
import Disclaimer from '../components/Disclaimer';
import Accordion from '../components/Accordion';
import SiteNav from '../components/SiteNav';

const PERSONA_LABELS = {
  sahm: 'Stay-at-Home Mom',
  working_mom: 'Working Mom',
  professional: 'Working Woman',
  midlife: 'Midlife',
  back_in_court: 'Back in Court',
  other: 'Custom Situation',
};

const COURT_ISSUE_LABELS = { finances: 'Finances', custody: 'Custody', both: 'Finances & Custody' };
const COURT_LEVEL_LABELS = { district: 'District Court', appeals: 'Court of Appeals', supreme: 'State Supreme Court' };

export default function Dashboard() {
  const { user, token, logout, refreshUser, loading: authLoading } = useAuth();
  const [roadmap, setRoadmap] = useState(null);
  const [qa, setQA] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [activeTab, setActiveTab] = useState('roadmap');
  const [openQuestion, setOpenQuestion] = useState(null);
  const [submitQ, setSubmitQ] = useState('');
  const [submitSent, setSubmitSent] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', name: '' });
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const comingFromStripe = new URLSearchParams(location.search).get('subscribed') === 'true';

    const init = async () => {
      let currentUser = user;
      if (comingFromStripe) {
        const sessionId = new URLSearchParams(location.search).get('session_id');
        console.log('Coming from Stripe, session_id:', sessionId, 'token:', !!token);
        if (sessionId) {
          try {
            const verifyRes = await axios.post(`${API}/api/subscription/verify-session`, { session_id: sessionId }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Verify response:', verifyRes.data);
          } catch (e) {
            console.error('Verify-session failed:', e?.response?.data || e.message);
          }
        }
        // Refresh user from DB after verification
        try {
          const { data } = await axios.get(`${API}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          currentUser = data.user;
          console.log('Fresh user after verify:', currentUser);
          await refreshUser();
        } catch (e) {
          console.error('Auth/me failed:', e?.response?.data || e.message);
        }
      }

      if (!currentUser?.quiz_completed) {
        navigate('/quiz');
        return;
      }

      Promise.all([
        axios.get(`${API}/api/quiz/roadmap`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/quiz/qa`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/content/announcements`),
      ])
        .then(([roadmapRes, qaRes, annRes]) => {
          setRoadmap(roadmapRes.data.roadmap);
          setQA(qaRes.data.qa);
          setAnnouncements(annRes.data.announcements || []);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    };

    if (token && !authLoading) init();
  }, [token, authLoading, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div style={styles.loadingPage}>
      <img src="/logo.svg" alt="Elephants Answering" style={styles.loadingLogo} />
      <p style={styles.loadingText}>Loading your roadmap...</p>
    </div>
  );

  const personaLabel = PERSONA_LABELS[roadmap?.persona] || '';

  return (
    <div style={styles.container}>
      <SiteNav />

      <main style={styles.main}>
        <div style={styles.pageHeader}>
          <p style={styles.eyebrow}>My Dashboard</p>
          <h2 style={styles.pageTitle}>My Account</h2>
        </div>

        {announcements.length > 0 && (
          <div style={styles.announcements}>
            <span style={styles.announcementLabel}>✦ What's New</span>
            {announcements.map(a => (
              <p key={a.id} style={styles.announcementItem}>{a.message}</p>
            ))}
          </div>
        )}

        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(activeTab === 'roadmap' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('roadmap')}
          >
            Your Roadmap
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'qa' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('qa')}
          >
            Q&A
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'stories' ? styles.tabActive : {}) }}
            onClick={() => navigate('/listen')}
          >
            Stories
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'resources' ? styles.tabActive : {}) }}
            onClick={() => navigate('/resources')}
          >
            Resources
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'feedback' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('feedback')}
          >
            Feedback
          </button>
        </div>

        {activeTab === 'roadmap' && roadmap?.persona === 'back_in_court' && (
          <div>
            <div style={styles.howItWorks}>
              <h3 style={styles.howTitle}>You're further along than most.</h3>
              <p style={styles.howStepDesc} >You've been through this before — a standard roadmap doesn't apply to where you are now. Instead, here are stories from women who've been back in court. More are on the way.</p>
              <div style={{marginTop: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '13px', fontFamily: fonts.body, color: colors.textMedium}}>
                {roadmap.has_lawyer === false && <span style={styles.bicTag}>No lawyer — navigating alone</span>}
                {roadmap.court_issue && <span style={styles.bicTag}>{COURT_ISSUE_LABELS[roadmap.court_issue]}</span>}
                {roadmap.court_level && <span style={styles.bicTag}>{COURT_LEVEL_LABELS[roadmap.court_level]}</span>}
              </div>
            </div>
            <div style={styles.storyGrid}>
              {[
                { title: 'Back In Court After Divorce', speaker: 'Community member', duration: '', desc: 'Coming soon.', available: false },
                { title: 'WTF, the Court of Appeals?!?', speaker: 'Community member', duration: '', desc: 'Coming soon.', available: false },
              ].map(s => (
                <div key={s.title} style={styles.storyCard}>
                  <div style={{display:'flex', gap:'8px', alignItems:'center', marginBottom:'10px'}}>
                    {s.available
                      ? <span style={styles.freeBadge}>Free</span>
                      : <span style={styles.comingSoonBadge}>Coming Soon</span>}
                    {s.duration && <span style={styles.duration}>{s.duration}</span>}
                  </div>
                  <h4 style={styles.storyTitle}>{s.title}</h4>
                  <p style={styles.storySpeaker}>{s.speaker}</p>
                  <p style={styles.storyDesc}>{s.desc}</p>
                  {s.available && (
                    <button style={styles.listenBtn} onClick={() => navigate('/listen')}>Listen Now →</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'roadmap' && roadmap?.persona !== 'back_in_court' && (
          <div>
          <div style={styles.howItWorks}>
            <h3 style={styles.howTitle}>How It Works</h3>
            <p style={styles.howStepDesc}>Thanks for sharing a little bit about your situation. Based on your responses to the quiz questions, a 4-phase roadmap has been created to help you prepare for your attorney, court and what might come next.</p>
          </div>
          <Accordion
            allowMultiple={true}
            defaultOpen={null}
            items={(roadmap?.phases || []).map((phase, index) => {
              const isLocked = index > 0 && user?.subscription_status !== 'active';
              return {
                label: `Phase ${phase.phase} — ${phase.title}${isLocked ? ' 🔒' : ''}`,
                children: isLocked ? (
                  <div style={styles.phaseLocked}>
                    <p style={styles.phaseLockedText}>Phases 2–4 are available to community members.</p>
                    <button style={styles.phaseLockedBtn} onClick={() => navigate('/subscribe')}>
                      Join the Community — Free for Early Members
                    </button>
                    <p style={styles.phaseLockedSub}>Cancel anytime</p>
                  </div>
                ) : (
                  <div style={styles.phaseContent}>
                    <ul style={styles.steps}>
                      {phase.steps.map((step, i) => (
                        <li key={i} style={styles.step}>
                          <span style={styles.stepArrow}>→</span>
                          {step}
                        </li>
                      ))}
                    </ul>
                    {phase.note && <p style={styles.phaseNote}>"{phase.note}"</p>}
                  </div>
                ),
              };
            })}
          />
          </div>
        )}

        {activeTab === 'qa' && (
          <div style={styles.qaContainer}>
            <p style={styles.qaIntro}>
              Real questions from women who've been through divorce — answered in plain language.
            </p>
            <Accordion
              allowMultiple={true}
              items={qa.map((category, ci) => ({
                label: category.category,
                children: (
                  <div>
                    {category.items.map((item, qi) => {
                      const key = `${ci}-${qi}`;
                      const isOpen = openQuestion === key;
                      return (
                        <div key={qi} style={styles.qaItem}>
                          <button
                            style={styles.qaQuestion}
                            onClick={() => setOpenQuestion(isOpen ? null : key)}
                          >
                            <span>{item.q}</span>
                            <span style={styles.qaToggle}>{isOpen ? '−' : '+'}</span>
                          </button>
                          {isOpen && <p style={styles.qaAnswer}>{item.a}</p>}
                        </div>
                      );
                    })}
                  </div>
                ),
              }))}
            />

            {/* Submit a question */}
            <div style={styles.submitBox}>
              <h3 style={styles.submitTitle}>What did we miss?</h3>
              <p style={styles.submitDesc}>Submit a question — if you have it, someone else does too. We'll add an answer within 48 hours.</p>
              {submitSent ? (
                <p style={styles.submitThanks}>Got it. We'll add an answer within 48 hours. — Amy</p>
              ) : (
                <form onSubmit={async e => {
                  e.preventDefault();
                  setSubmitLoading(true);
                  try {
                    await axios.post(`${API}/api/quiz/submit-question`, { question: submitQ }, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    setSubmitSent(true);
                    setSubmitQ('');
                  } catch {
                    // silent fail — show thanks anyway
                    setSubmitSent(true);
                  } finally {
                    setSubmitLoading(false);
                  }
                }} style={styles.submitForm}>
                  <textarea
                    style={styles.submitInput}
                    placeholder="What's your question?"
                    value={submitQ}
                    onChange={e => setSubmitQ(e.target.value)}
                    rows={3}
                    required
                  />
                  <button style={styles.submitBtn} type="submit" disabled={submitLoading}>
                    {submitLoading ? 'Sending...' : 'Submit Question'}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}
        {activeTab === 'feedback' && (
          <div style={styles.submitBox}>
            <h3 style={styles.submitTitle}>We're new here. Feedback is welcome.</h3>
            <p style={styles.submitDesc}>Tell us what's working, what's missing, or what you wish existed.</p>
            {feedbackSent ? (
              <p style={styles.submitThanks}>Thank you — we read every note. — Amy</p>
            ) : (
              <form onSubmit={async e => {
                e.preventDefault();
                setFeedbackLoading(true);
                try {
                  await axios.post(`${API}/api/content/feedback`, feedback);
                  setFeedbackSent(true);
                } catch {}
                setFeedbackLoading(false);
              }} style={styles.submitForm}>
                <textarea
                  style={styles.submitInput}
                  placeholder="Your feedback..."
                  value={feedback.message}
                  onChange={e => setFeedback({ ...feedback, message: e.target.value })}
                  rows={4}
                  required
                />
                <input
                  style={{ ...styles.submitInput, resize: 'none' }}
                  placeholder="Your name (optional)"
                  value={feedback.name}
                  onChange={e => setFeedback({ ...feedback, name: e.target.value })}
                />
                <button style={styles.submitBtn} type="submit" disabled={feedbackLoading}>
                  {feedbackLoading ? 'Sending...' : 'Send Feedback'}
                </button>
              </form>
            )}
          </div>
        )}
      </main>

      <Disclaimer />
      <footer style={styles.footer}>
        <p style={styles.footerText}>© 2026 Elephants Answering, PBC · Colorado Public Benefit Corporation</p>
      </footer>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: colors.cream, fontFamily: fonts.body, display: 'flex', flexDirection: 'column' },

  main: { maxWidth: '880px', margin: '0 auto', padding: '48px 20px', flex: 1, width: '100%', boxSizing: 'border-box' },

  pageHeader: { marginBottom: '36px' },
  eyebrow: { fontFamily: fonts.body, color: colors.rose, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '10px' },
  pageTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '30px', marginBottom: '10px', fontWeight: '600', letterSpacing: '0.3px' },
  roadmapNote: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '12px', lineHeight: '1.7', marginBottom: '8px', backgroundColor: colors.offWhite, border: `1px solid ${colors.peach}`, padding: '10px 14px' },
  pageSubtitle: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', fontStyle: 'italic', lineHeight: '1.6' },

  announcements: { backgroundColor: colors.white, border: `1px solid ${colors.peach}`, borderLeft: `4px solid ${colors.gold}`, padding: '16px 20px', marginBottom: '28px', display: 'flex', flexDirection: 'column', gap: '6px' },
  announcementLabel: { fontFamily: fonts.body, color: colors.gold, fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' },
  announcementItem: { fontFamily: fonts.body, color: colors.navy, fontSize: '14px', lineHeight: '1.7', margin: 0 },

  tabs: { display: 'flex', gap: '0', marginBottom: '36px', borderBottom: `2px solid ${colors.peach}` },
  tab: { padding: '12px 28px', backgroundColor: 'transparent', border: 'none', borderBottom: '3px solid transparent', marginBottom: '-2px', fontSize: '14px', cursor: 'pointer', color: colors.textMedium, fontFamily: fonts.body, letterSpacing: '0.3px' },
  tabActive: { color: colors.navy, fontWeight: '600', borderBottom: `3px solid ${colors.rose}` },

  howItWorks: { marginBottom: '36px', padding: '28px 32px', backgroundColor: colors.white, border: `1px solid ${colors.peach}` },
  howTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '16px', fontWeight: '600', marginBottom: '20px', letterSpacing: '0.3px' },
  howSteps: { display: 'flex', gap: '20px', flexWrap: 'wrap' },
  howStep: { flex: '1', minWidth: '180px' },
  howStepNum: { fontFamily: fonts.heading, fontSize: '22px', color: colors.rose, fontWeight: '600', marginBottom: '6px' },
  howStepTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '13px', fontWeight: '600', marginBottom: '6px' },
  howStepDesc: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '12px', lineHeight: '1.7' },

  phaseLocked: { padding: '28px 24px', textAlign: 'center' },
  phaseLockedText: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', marginBottom: '20px', lineHeight: '1.7' },
  phaseLockedBtn: { backgroundColor: colors.rose, color: colors.white, border: 'none', padding: '14px 32px', fontSize: '14px', fontFamily: fonts.body, fontWeight: '600', cursor: 'pointer', letterSpacing: '0.5px' },
  phaseLockedSub: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '12px', marginTop: '10px' },

  phaseContent: { paddingTop: '8px' },
  steps: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' },
  step: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', lineHeight: '1.6', display: 'flex', gap: '12px', alignItems: 'flex-start' },
  stepArrow: { color: colors.rose, fontWeight: 'bold', flexShrink: 0, marginTop: '2px' },
  phaseNote: { marginTop: '24px', fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', fontStyle: 'italic', borderTop: `1px solid ${colors.peach}`, paddingTop: '20px', lineHeight: '1.7' },

  qaContainer: { display: 'flex', flexDirection: 'column', gap: '24px' },
  qaIntro: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', fontStyle: 'italic', marginBottom: '8px' },
  qaCategory: { backgroundColor: colors.white, padding: '32px', border: `1px solid ${colors.peach}` },
  qaCategoryTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '14px', fontWeight: '600', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '1px' },
  qaItem: { borderBottom: `1px solid ${colors.peach}`, paddingBottom: '4px', marginBottom: '4px' },
  qaQuestion: { width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: '14px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: colors.navy, fontSize: '14px', fontWeight: '600', fontFamily: fonts.body, gap: '16px', lineHeight: '1.5' },
  qaToggle: { color: colors.rose, fontSize: '20px', flexShrink: 0, fontWeight: '300' },
  qaAnswer: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', lineHeight: '1.8', padding: '4px 0 20px 0' },

  submitBox: { marginTop: '48px', padding: '32px', backgroundColor: colors.white, border: `1px solid ${colors.peach}`, borderTop: `3px solid ${colors.rose}` },
  submitTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '16px', fontWeight: '600', marginBottom: '8px', letterSpacing: '0.3px' },
  submitDesc: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', lineHeight: '1.8', marginBottom: '20px', fontStyle: 'italic' },
  submitForm: { display: 'flex', flexDirection: 'column', gap: '12px' },
  submitInput: { padding: '12px 16px', border: `1px solid ${colors.peach}`, backgroundColor: colors.cream, color: colors.navy, fontSize: '14px', fontFamily: fonts.body, outline: 'none', resize: 'vertical', borderRadius: 0 },
  submitBtn: { alignSelf: 'flex-start', padding: '10px 24px', backgroundColor: colors.rose, color: colors.white, border: 'none', fontSize: '13px', fontFamily: fonts.body, fontWeight: '600', cursor: 'pointer', letterSpacing: '0.5px' },
  submitThanks: { fontFamily: fonts.body, color: colors.navy, fontSize: '14px', fontStyle: 'italic', lineHeight: '1.8' },

  bicTag: { backgroundColor: colors.offWhite, border: `1px solid ${colors.peach}`, padding: '4px 10px', fontSize: '12px', fontFamily: fonts.body, color: colors.textMedium },
  storyGrid: { display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '24px' },
  storyCard: { backgroundColor: colors.white, border: `1px solid ${colors.peach}`, borderTop: `3px solid ${colors.rose}`, padding: '24px 28px' },
  freeBadge: { fontSize: '10px', fontFamily: fonts.body, fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: colors.rose, border: `1px solid ${colors.rose}`, padding: '2px 8px' },
  comingSoonBadge: { fontSize: '10px', fontFamily: fonts.body, fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: colors.textMedium, border: `1px solid ${colors.peach}`, padding: '2px 8px' },
  duration: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '12px' },
  storyTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '16px', fontWeight: '600', marginBottom: '4px' },
  storySpeaker: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '12px', letterSpacing: '0.3px', marginBottom: '8px', textTransform: 'uppercase' },
  storyDesc: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', lineHeight: '1.7', marginBottom: '16px' },
  listenBtn: { backgroundColor: 'transparent', border: `1px solid ${colors.rose}`, color: colors.rose, padding: '8px 20px', fontSize: '13px', fontFamily: fonts.body, fontWeight: '600', cursor: 'pointer', letterSpacing: '0.3px' },

  footer: { backgroundColor: colors.navy, padding: '20px', textAlign: 'center' },
  footerText: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.3)', fontSize: '11px', letterSpacing: '0.3px' },

  loadingPage: { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: colors.cream, gap: '16px' },
  loadingLogo: { height: '56px', opacity: 0.5 },
  loadingText: { fontFamily: fonts.heading, fontSize: '18px', color: colors.navy, letterSpacing: '0.3px' },
};
